import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAthlete } from '@/context/AthleteContext';
import { Target, User, Users, ChevronDown } from 'lucide-react';
import axios from 'axios';
import PlayerDevelopmentProfile from '@/components/PlayerDevelopmentProfile';
import DevelopmentRadarChart from '@/components/DevelopmentRadarChart';
import DevelopmentHistory from '@/components/DevelopmentHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Skills organized by category
const SKILL_CATEGORIES = [
  {
    category: 'TECHNICAL',
    color: 'text-emerald-400',
    skills: [
      { key: 'ball_control', label: 'Ball Control' },
      { key: 'passing', label: 'Passing' },
      { key: 'dribbling', label: 'Dribbling' },
      { key: 'first_touch', label: 'First Touch' },
      { key: 'finishing', label: 'Finishing' },
      { key: 'shooting', label: 'Shooting' },
    ]
  },
  {
    category: 'PHYSICAL',
    color: 'text-blue-400',
    skills: [
      { key: 'speed', label: 'Speed' },
      { key: 'acceleration', label: 'Acceleration' },
      { key: 'agility', label: 'Agility' },
      { key: 'coordination', label: 'Coordination' },
      { key: 'balance', label: 'Balance' },
      { key: 'explosiveness', label: 'Explosiveness' },
      { key: 'endurance', label: 'Endurance' },
    ]
  },
  {
    category: 'TACTICAL / IQ',
    color: 'text-yellow-400',
    skills: [
      { key: 'positioning', label: 'Positioning' },
      { key: 'decision_making', label: 'Decision Making' },
      { key: 'vision', label: 'Vision' },
      { key: 'game_awareness', label: 'Game Awareness' },
      { key: 'reaction_time', label: 'Reaction Time' },
    ]
  }
];

const ParentTechnicalDev = () => {
  const { getAuthHeaders } = useAuth();
  const { athlete } = useAthlete();
  const [techDev, setTechDev] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [showCoachSelector, setShowCoachSelector] = useState(false);

  // Fetch available coaches for this athlete
  useEffect(() => {
    const fetchCoaches = async () => {
      if (!athlete) return;
      
      const availableCoaches = [];
      
      // Check for private coach
      if (athlete.coach_id) {
        availableCoaches.push({
          id: athlete.coach_id,
          type: 'private',
          name: athlete.coach_name || 'Private Coach',
          icon: User
        });
      }
      
      // Check for team coaches
      try {
        const teamsRes = await axios.get(`${API}/parent/athlete/${athlete.id}/teams`, { headers: getAuthHeaders() });
        if (teamsRes.data?.teams) {
          teamsRes.data.teams.forEach(team => {
            if (team.coach_id) {
              availableCoaches.push({
                id: team.coach_id,
                type: 'team',
                name: team.coach_name || `${team.team_name} Coach`,
                teamName: team.team_name,
                teamId: team.team_id,
                icon: Users
              });
            }
          });
        }
      } catch (err) {
        console.log('No team data');
      }
      
      setCoaches(availableCoaches);
      if (availableCoaches.length > 0 && !selectedCoach) {
        setSelectedCoach(availableCoaches[0]);
      }
    };
    
    fetchCoaches();
  }, [athlete]);

  // Fetch technical development based on selected coach
  useEffect(() => {
    const fetchData = async () => {
      if (!athlete || !selectedCoach) return;
      setLoading(true);
      try {
        let url = `${API}/athletes/${athlete.id}/technical-development`;
        
        // If team coach, fetch team-specific tech dev
        if (selectedCoach.type === 'team' && selectedCoach.teamId) {
          url = `${API}/parent/team/${selectedCoach.teamId}/athlete/${athlete.id}/technical-development`;
        }
        
        const techRes = await axios.get(url, { headers: getAuthHeaders() });
        setTechDev(techRes.data);
      } catch (error) {
        console.error('Failed to fetch technical development:', error);
        setTechDev(null);
      }
      setLoading(false);
    };
    fetchData();
  }, [athlete, selectedCoach, getAuthHeaders]);

  const getStatusColor = (status) => {
    const s = (status || '').toString().toLowerCase();
    if (s === 'proficient') return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (s === 'developing') return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    if (s === 'focus') return 'bg-red-500/20 text-red-400 border-red-500/30';
    // Not assessed / missing — neutral muted, not alarming red.
    return 'bg-muted/40 text-muted-foreground border-border/40';
  };

  if (loading && !techDev) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-technical-dev">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Technical Development</h1>
          <p className="text-muted-foreground">{athlete?.name}'s skill progression</p>
        </div>
        
        {/* Coach Selector */}
        {coaches.length > 1 && (
          <div className="relative">
            <button
              onClick={() => setShowCoachSelector(!showCoachSelector)}
              className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors"
              data-testid="coach-selector"
            >
              {selectedCoach && (
                <>
                  <selectedCoach.icon size={18} className="text-primary" />
                  <span className="text-sm font-medium">{selectedCoach.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-primary/20 text-primary">
                    {selectedCoach.type === 'private' ? 'Private' : 'Team'}
                  </span>
                </>
              )}
              <ChevronDown size={16} className={`transition-transform ${showCoachSelector ? 'rotate-180' : ''}`} />
            </button>
            
            {showCoachSelector && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-card border border-border rounded-lg shadow-lg z-50">
                <p className="px-3 py-2 text-xs text-muted-foreground border-b border-border">Select Coach View</p>
                {coaches.map((coach) => (
                  <button
                    key={`${coach.type}-${coach.id}`}
                    onClick={() => {
                      setSelectedCoach(coach);
                      setShowCoachSelector(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-3 hover:bg-muted/50 transition-colors ${
                      selectedCoach?.id === coach.id && selectedCoach?.type === coach.type ? 'bg-primary/10' : ''
                    }`}
                  >
                    <coach.icon size={18} className={selectedCoach?.id === coach.id ? 'text-primary' : 'text-muted-foreground'} />
                    <div className="text-left">
                      <p className="text-sm font-medium">{coach.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {coach.type === 'private' ? 'Private Coach' : coach.teamName}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {!athlete || !techDev ? (
        <>
          <div className="text-center py-12 bg-card border border-border rounded-lg">
            <p className="text-muted-foreground">
              {selectedCoach ? `No technical development data from ${selectedCoach.name}` : 'No technical development data found'}
            </p>
          </div>

          {/* Development Story is still useful even without a full evaluation — it
              gives parents context the moment a coach starts logging Quick Updates. */}
          {athlete && (
            <div className="pt-4">
              <DevelopmentHistory athleteId={athlete.id} athleteName={athlete.name} />
            </div>
          )}
        </>
      ) : (
        <>
          {/* Radar Chart - Development Visualization */}
          <DevelopmentRadarChart techDev={techDev} />

          {/* Player Development Profile Summary */}
          <PlayerDevelopmentProfile techDev={techDev} />

          {/* Status Legend */}
          <div className="bg-muted/30 p-3 rounded-sm">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="text-muted-foreground">Status:</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span> Focus</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400"></span> Developing</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400"></span> Proficient</span>
            </div>
          </div>

          {SKILL_CATEGORIES.map((category) => (
            <div key={category.category}>
              <div className="flex items-center gap-3 mb-4">
                <h2 className={`font-heading text-lg uppercase tracking-wider ${category.color}`}>
                  {category.category}
                </h2>
                <div className="flex-1 h-px bg-border"></div>
              </div>
              <div className="space-y-3 mb-6">
                {category.skills.map((skill) => {
                  const data = techDev[skill.key];
                  return (
                    <div key={skill.key} className="bg-card border border-border/40 p-4 rounded-sm">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <Target className="text-primary" size={18} />
                          <h3 className="font-semibold">{skill.label}</h3>
                        </div>
                        <span className={`px-3 py-1 rounded border text-sm ${getStatusColor(data?.status)}`}>
                          {data?.status ? data.status.charAt(0).toUpperCase() + data.status.slice(1).toLowerCase() : 'Not Assessed'}
                        </span>
                      </div>
                      {data?.notes && (
                        <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-sm">
                          <span className="text-primary font-medium">Coach Notes:</span> {data.notes}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <p className="text-xs text-muted-foreground text-center">
            Last updated: {techDev.updated_at ? new Date(techDev.updated_at).toLocaleDateString() : 'N/A'}
            {selectedCoach && ` • ${selectedCoach.name}`}
          </p>

          {/* Development Story Timeline — longitudinal evolution from Quick Updates */}
          <div className="pt-4 border-t border-border/30">
            <DevelopmentHistory athleteId={athlete.id} athleteName={athlete.name} />
          </div>
        </>
      )}
    </div>
  );
};

export default ParentTechnicalDev;
